/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type {
  ListenerEffectAPI,
  ThunkDispatch,
  UnknownAction,
} from '@reduxjs/toolkit';
import { dateUtils } from '@opengeoweb/shared';
import type { AnimationRange } from '@opengeoweb/time-slider';
import {
  AnimationLengthMode,
  isAnchoredMode,
  isValidAnimationLength,
  isWithinAnimationStepLimit,
  settingRange,
} from '@opengeoweb/time-slider';
import { layerActions, layerSelectors } from '../layer';
import type { WebMapStateModuleState } from '../types';
import { mapActions } from './reducer';
import {
  getAnimationAnchorInputs,
  getAnimationEndTime,
  getAnimationLength,
  getAnimationStartTime,
  getAutoTimeStepLayerId,
  getMapTimeStep,
} from './selectors';

type MapListenerApi = ListenerEffectAPI<
  WebMapStateModuleState,
  ThunkDispatch<WebMapStateModuleState, unknown, UnknownAction>,
  unknown
>;

export const ANIMATION_CLOCK_INTERVAL = 1000 * 60;

/** Auto is left out: its selector derives the range from the leading layer. */
const resolveMapAnimationRange = (
  state: WebMapStateModuleState,
  mapId: string,
): AnimationRange | undefined => {
  const animationLength = getAnimationLength(state, mapId);
  if (!animationLength || animationLength.mode === AnimationLengthMode.Auto) {
    return undefined;
  }
  const anchorInputs = getAnimationAnchorInputs(state, mapId);
  const isUsableRange =
    isValidAnimationLength(animationLength, anchorInputs) &&
    isWithinAnimationStepLimit(
      animationLength,
      getMapTimeStep(state, mapId),
      anchorInputs,
    );
  return isUsableRange
    ? settingRange(animationLength, anchorInputs)
    : undefined;
};

const isSameInstant = (time: string, storedTime?: string): boolean =>
  !!storedTime &&
  dateUtils.utc(time).getTime() === dateUtils.utc(storedTime).getTime();

const isStoredRange = (
  state: WebMapStateModuleState,
  mapId: string,
  { animationStartTime, animationEndTime }: AnimationRange,
): boolean =>
  isSameInstant(animationStartTime, getAnimationStartTime(state, mapId)) &&
  isSameInstant(animationEndTime, getAnimationEndTime(state, mapId));

export const reconcileAnimationRange = (
  mapId: string,
  listenerApi: MapListenerApi,
  skipSync = false,
): void => {
  const range = resolveMapAnimationRange(listenerApi.getState(), mapId);
  if (range && !isStoredRange(listenerApi.getState(), mapId, range)) {
    listenerApi.dispatch(
      mapActions.refreshAnimationRange({
        mapId,
        ...range,
        ...(skipSync && { skipSync }),
      }),
    );
  }
};

const mapIdOfLeadingLayer = (
  state: WebMapStateModuleState,
  layerId?: string,
): string | undefined => {
  const mapId = layerSelectors.getLayerById(state, layerId)?.mapId;
  return mapId && getAutoTimeStepLayerId(state, mapId) === layerId
    ? mapId
    : undefined;
};

export const mapIdToReconcile = (
  action: UnknownAction,
  state: WebMapStateModuleState,
): string | undefined => {
  if (layerActions.onUpdateLayerInformation.match(action)) {
    return mapIdOfLeadingLayer(state, action.payload.layerDimensions?.layerId);
  }
  if (layerActions.layerChangeDimension.match(action)) {
    return action.payload.dimension.name === 'reference_time'
      ? mapIdOfLeadingLayer(state, action.payload.layerId)
      : undefined;
  }
  return (action.payload as { mapId?: string }).mapId;
};

export const isSyncSkippedBy = (action: UnknownAction): boolean =>
  mapActions.setAnimationLength.match(action) && !!action.payload.skipSync;

export const hasAnchoredAnimationLength = (
  state: WebMapStateModuleState,
  mapId: string,
): boolean => isAnchoredMode(getAnimationLength(state, mapId)?.mode);

export const endsAnimationClockOf =
  (mapId: string) =>
  (action: UnknownAction): boolean =>
    (mapActions.setAnimationLength.match(action) ||
      mapActions.unregisterMap.match(action)) &&
    action.payload.mapId === mapId;
